import React from 'react';
import { DialogContent, DialogOverlay } from '@reach/dialog';

import Button from '../Button';
import Icon from '../Icon';
import { classnames } from '../../utils';

import '@reach/dialog/styles.css';
import styles from './LovableModal.module.scss';

/**
 * Modal component with Lovable design styling.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {(e: any) => void} props.onDismiss
 * @param {boolean} props.isOpen
 * @param {string} props.title
 * @param {string} [props.className='']
 * @param {(e: any) => void} [props.confirmationAction]
 * @param {string} [props.cancel] - Text for the cancel button
 * @param {string} [props.confirm] - Text for the confirm button
 * @param {boolean} [props.showDismissButton=true] - Whether to show the dismiss button
 * @returns {JSX.Element}
 */
const LovableModal = ({
	children,
	className = '',
	onDismiss,
	isOpen,
	title,
	confirmationAction,
	cancel,
	confirm,
	showDismissButton = true,
	...props
}) => {
	const confirmationActionAndClose = (e) => {
		onDismiss?.(e);
		confirmationAction?.(e);
	};

	/**
	 * Modal exit button.
	 */
	const ExitButton = ({ onClick, className: btnClassName = '' }) => (
		<button className={classnames(styles.exitButton, btnClassName)} onClick={onClick}>
			<Icon>clear</Icon>
		</button>
	);

	return (
		<DialogOverlay className={styles.overlay} onDismiss={onDismiss} isOpen={isOpen} {...props}>
			<DialogContent className={classnames(styles.container, className)}>
				<div className={styles.title}>
					<h2>{title}</h2>
					<ExitButton onClick={onDismiss} />
				</div>
				<div className={styles.content}>{children}</div>
				<div className={styles.buttons}>
					{/* this wrapper div is actually necessary for styling */}
					<div>
						{cancel ? (
							<Button data-cy={`modal-cancel`} onClick={confirmationActionAndClose} cancel>
								{cancel}
							</Button>
						) : null}
						{confirm ? (
							<Button data-cy={`modal-confirm`} onClick={confirmationActionAndClose} confirm>
								{confirm}
							</Button>
						) : null}
						{showDismissButton ? <Button onClick={onDismiss}>Cancel</Button> : null}
					</div>
				</div>
			</DialogContent>
		</DialogOverlay>
	);
};

export { LovableModal };
